<script>
    import { onMount, tick } from "svelte";
    import { theme } from "$lib/utils/theme.svelte.ts";

    const SOUND_PREFERENCE_KEY = "dinglandia:sound-enabled";
    const DEFAULT_SOUND_ENABLED = false;

    let isOn = $state(false);
    let buttonEl;
    const isDark = $derived(theme.resolved === "dark");

    function readInitialPreference() {
        try {
            const stored = localStorage.getItem(SOUND_PREFERENCE_KEY);
            if (stored === null) return DEFAULT_SOUND_ENABLED;
            return stored === "true";
        } catch {
            return DEFAULT_SOUND_ENABLED;
        }
    }

    onMount(() => {
        isOn = readInitialPreference();

        // Sync this specific toggle instance with the shared runtime sound state.
        const btn = buttonEl;
        if (!btn) return;

        const syncFromDom = () => {
            const ariaPressed = btn.getAttribute("aria-pressed");
            const nextState =
                ariaPressed === null
                    ? btn.classList.contains("is-on")
                    : ariaPressed === "true";
            if (nextState !== isOn) {
                isOn = nextState;
            }
        };

        // Wait for Svelte to flush the DOM update from readInitialPreference()
        // before reading aria-pressed back, otherwise we read the stale initial
        // "false" and overwrite the correct value.
        tick().then(syncFromDom);

        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (
                    m.attributeName === "class" ||
                    m.attributeName === "aria-pressed"
                ) {
                    syncFromDom();
                }
            }
        });
        observer.observe(btn, {
            attributes: true,
            attributeFilter: ["class", "aria-pressed"],
        });

        return () => observer.disconnect();
    });
</script>

<button
    bind:this={buttonEl}
    type="button"
    class="sound-toggle"
    class:is-on={isOn}
    class:is-off={!isOn}
    data-sound-toggle
    aria-pressed={isOn}
    aria-label={isOn ? "Mute lightning sound" : "Unmute lightning sound"}
    title="Toggle Sound"
    style="--on: {isOn ? 1 : 0}; --dark: {isDark ? 1 : 0}"
>
    <span class="stoggle__content">
        <!-- Zzz backdrop (visible when OFF — like clouds in ThemeToggle) -->
        <svg
            aria-hidden="true"
            class="stoggle__backdrop"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 290 120"
        >
            <g class="zzz-marks">
                <text
                    x="205"
                    y="38"
                    font-size="22"
                    font-weight="bold"
                    font-family="system-ui, sans-serif">Z</text
                >
                <text
                    x="225"
                    y="55"
                    font-size="16"
                    font-weight="bold"
                    font-family="system-ui, sans-serif">z</text
                >
                <text
                    x="240"
                    y="42"
                    font-size="11"
                    font-weight="bold"
                    font-family="system-ui, sans-serif">z</text
                >
            </g>
        </svg>

        <!-- Musical notes backdrop (visible when ON — like stars in ThemeToggle) -->
        <svg
            aria-hidden="true"
            class="stoggle__backdrop"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 290 120"
        >
            <g class="notes">
                <!-- Eighth notes and quarter notes scattered like stars -->
                <g
                    ><path
                        d="M62 18c0-1.1.9-2 2-2h1v10a3 3 0 1 1-2-2.8V18h-1z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M44 52c0-1.1.9-2 2-2h1v8a2.5 2.5 0 1 1-2-2.4V52h-1z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M70 72c0-.8.7-1.5 1.5-1.5h.5v7a2 2 0 1 1-1.5-1.9V72h-.5z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M35 82c0-1.1.9-2 2-2h1v9a2.5 2.5 0 1 1-2-2.4V82h-1z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M120 22c0-.8.7-1.5 1.5-1.5h.5v6.5a2 2 0 1 1-1.5-1.9V22h-.5z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M108 55c0-1.1.9-2 2-2h1v10a3 3 0 1 1-2-2.8V55h-1z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M145 35c0-1.1.9-2 2-2h1v9a2.5 2.5 0 1 1-2-2.4V35h-1z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M155 65c0-.8.7-1.5 1.5-1.5h.5v7a2 2 0 1 1-1.5-1.9V65h-.5z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M130 85c0-1 .8-1.8 1.8-1.8h.7v8a2.2 2.2 0 1 1-1.8-2.1V85h-.7z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M50 36c0-.6.5-1 1-1h.5v5a1.5 1.5 0 1 1-1-1.4V36h-.5z"
                        fill="#fff"
                    /></g
                >
                <g
                    ><path
                        d="M95 40c0-.8.7-1.5 1.5-1.5h.5v6.5a2 2 0 1 1-1.5-1.9V40h-.5z"
                        fill="#fff"
                    /></g
                >
            </g>
        </svg>

        <!-- Sliding indicator disc with speaker icon -->
        <span class="stoggle__indicator-wrapper">
            <span class="stoggle__indicator">
                <span class="stoggle__star">
                    <span class="stoggle__disc">
                        <!-- Speaker body -->
                        <svg
                            class="stoggle__speaker"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path
                                class="speaker-body"
                                d="M5 9h3.5l4.5-4v14l-4.5-4H5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z"
                            />
                        </svg>
                        <!-- Mute slash (slides in when OFF, like moon slides over sun) -->
                        <span class="mute-slash"></span>
                    </span>
                </span>
            </span>
        </span>
    </span>
    <span class="visually-hidden" data-sound-label
        >{isOn ? "Sound on" : "Sound off"}</span
    >
</button>

<style>
    .sound-toggle {
        -webkit-tap-highlight-color: transparent;
        --slide-ease: cubic-bezier(0.4, -0.3, 0.6, 1.3);
        --easing: var(--slide-ease);
        --speed: 0.5s;
        --width: clamp(46px, 8vw, 72px);
        --ar: 8 / 3;
        --disc-on: hsl(45 80% 55%);
        --disc-off: hsl(215 15% 75%);

        width: var(--width);
        aspect-ratio: var(--ar);
        border-radius: 100vh;
        border: 0;
        position: relative;
        padding: 0;
        overflow: hidden;
        cursor: pointer;
        transition: background var(--speed) var(--easing);
        background: hsl(
            calc(204 + (var(--dark) * 25) + (var(--on) * -9))
                calc((53 - (var(--dark) * 28) + (var(--on) * 15)) * 1%)
                calc((47 - (var(--dark) * 31) + (var(--on) * -6)) * 1%)
        );
        box-shadow:
            calc(var(--width) * 0) calc(var(--width) * 0.02)
                calc(var(--width) * 0.01) calc(var(--width) * -0.0025)
                hsl(210 10% 100% / 0.95),
            calc(var(--width) * 0) calc(var(--width) * -0.02)
                calc(var(--width) * 0.01) calc(var(--width) * -0.0025)
                hsl(210 10% 10% / 0.2),
            calc(var(--width) * 0) calc(var(--width) * 0.02)
                calc(var(--width) * 0.5) 0 hsl(210 10% 100% / 0.15);
        isolation: isolate;
        will-change: transform;
        transform: translate3d(0, 0, 0);
        z-index: 10;
    }

    .sound-toggle::after {
        content: "";
        position: absolute;
        inset: 0;
        box-shadow:
            calc(var(--width) * 0) calc(var(--width) * -0.025)
                calc(var(--width) * 0.025) 0 hsl(210 10% 10% / 0.15) inset,
            calc(var(--width) * 0) calc(var(--width) * 0.025)
                calc(var(--width) * 0.025) 0 hsl(210 10% 10% / 0.65) inset;
        border-radius: 100vh;
        pointer-events: none;
    }

    .stoggle__content {
        position: absolute;
        inset: 0;
        overflow: hidden;
        border-radius: 100vh;
        display: block;
        clip-path: inset(0 0 0 0 round 100vh);
        container-type: inline-size;
    }

    /* ─── Backdrops ─── */
    .stoggle__backdrop {
        overflow: visible !important;
        position: absolute;
        bottom: 0;
        width: 100%;
        left: 0;
    }

    /* Zzz marks — slide up when ON (like clouds slide away in ThemeToggle) */
    .stoggle__backdrop:first-of-type {
        transition: translate var(--speed) var(--easing);
        translate: 0 calc(var(--on) * (100% - (3 / 8 * var(--width))));
    }

    .zzz-marks {
        fill: hsl(0 0% 100% / 0.85);
    }

    .zzz-marks text:nth-child(1) {
        opacity: 0.9;
    }
    .zzz-marks text:nth-child(2) {
        opacity: 0.7;
    }
    .zzz-marks text:nth-child(3) {
        opacity: 0.5;
    }

    /* Musical notes — scale in like stars */
    .notes g {
        transform-box: fill-box;
        transform-origin: 50% 50%;
    }

    .notes path {
        transform-box: fill-box;
        transform-origin: 25% 50%;
        scale: calc(0.25 + (var(--on) * 0.75));
        transition: scale var(--speed) calc(var(--speed) * 0.5) var(--easing);
    }

    [aria-pressed="false"] .notes path {
        transition-delay: 0s;
    }

    /* Note twinkle animations (like star twinkle) */
    .notes g:nth-child(2) {
        animation: note-twinkle 4s -2s infinite;
    }
    .notes g:nth-child(6) {
        animation: note-twinkle 5s -1s infinite;
    }
    .notes g:nth-child(9) {
        animation: note-twinkle 4.5s -3s infinite;
    }
    .notes g:nth-child(4) {
        animation: note-twinkle 6s -0.5s infinite;
    }

    @keyframes note-twinkle {
        0%,
        40%,
        60%,
        100% {
            transform: scale(1);
        }
        50% {
            transform: scale(0);
        }
    }

    /* Zzz float animation when visible */
    [aria-pressed="false"] .zzz-marks text:nth-child(1) {
        animation: zzz-float 3s 0s ease-in-out infinite;
    }
    [aria-pressed="false"] .zzz-marks text:nth-child(2) {
        animation: zzz-float 3.5s 0.4s ease-in-out infinite;
    }
    [aria-pressed="false"] .zzz-marks text:nth-child(3) {
        animation: zzz-float 2.8s 0.8s ease-in-out infinite;
    }

    @keyframes zzz-float {
        0%,
        100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-3px);
        }
    }

    /* Zzz timing when toggling off — bouncy ease (like cloud timing) */
    [aria-pressed="false"] .stoggle__backdrop:first-of-type {
        transition-timing-function: cubic-bezier(0.2, -0.6, 0.7, 1.6);
    }

    /* ─── Sliding indicator disc ─── */
    .stoggle__indicator-wrapper {
        position: absolute;
        inset: 0;
    }

    .stoggle__indicator {
        height: 100%;
        aspect-ratio: 1;
        border-radius: 0%;
        display: grid;
        place-items: center;
        padding: 3%;
        transition: translate var(--speed) var(--slide-ease);
        translate: calc(var(--on) * (100cqi - 100%)) 0;
    }

    .stoggle__star {
        height: 100%;
        aspect-ratio: 1;
        border-radius: 50%;
        position: relative;
        transition: translate var(--speed) var(--easing);
        translate: calc((var(--on) * -10%) + 5%) 0;
    }

    /* Glow halo behind disc (like sun glow in ThemeToggle) */
    .stoggle__star::before {
        content: "";
        z-index: -1;
        width: 356%;
        background:
            radial-gradient(
                hsl(195 80% 60% / calc(0.15 + (var(--on) * 0.15))) 40%,
                transparent 40.5%
            ),
            radial-gradient(
                    hsl(195 80% 60% / calc(0.1 + (var(--on) * 0.2))) 56%,
                    transparent 56.5%
                )
                hsl(195 80% 60% / calc(0.05 + (var(--on) * 0.15)));
        border-radius: 50%;
        aspect-ratio: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transition: translate var(--speed) var(--easing);
        translate: calc((50 - (var(--on) * 4)) * -1%) -50%;
    }

    /* Drop shadow behind disc */
    .stoggle__star::after {
        content: "";
        position: absolute;
        inset: 0;
        display: block;
        background: hsl(0 0% 0% / 0.5);
        filter: blur(4px);
        translate: 2% 4%;
        border-radius: 50%;
        z-index: -1;
    }

    .stoggle__disc {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        overflow: hidden;
        display: grid;
        place-items: center;
        background: var(--disc-on);
        transition: background var(--speed) var(--easing);
        box-shadow:
            calc(var(--width) * 0.01) calc(var(--width) * 0.01)
                calc(var(--width) * 0.02) 0 hsl(210 10% 100% / 0.95) inset,
            calc(var(--width) * -0.01) calc(var(--width) * -0.01)
                calc(var(--width) * 0.02) 0 hsl(210 10% 20% / 0.5) inset;
    }

    /* Mute slash — slides in from right when OFF (like moon sliding over sun) */
    .mute-slash {
        position: absolute;
        inset: -1%;
        border-radius: 50%;
        background: var(--disc-off);
        transition: translate var(--speed) ease-in-out;
        translate: calc(var(--on) * 100%) 0%;
        box-shadow:
            calc(var(--width) * 0.01) calc(var(--width) * 0.01)
                calc(var(--width) * 0.02) 0 hsl(210 10% 100% / 0.95) inset,
            calc(var(--width) * -0.01) calc(var(--width) * -0.01)
                calc(var(--width) * 0.02) 0 hsl(210 10% 10% / 0.95) inset;
    }

    /* Red mute line across the muted disc */
    .mute-slash::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 10%;
        width: 80%;
        height: 12%;
        background: hsl(0 70% 55%);
        border-radius: 100vh;
        transform: translateY(-50%) rotate(-45deg);
        box-shadow: 0 1px 2px hsl(0 0% 0% / 0.3);
    }

    /* Speaker icon */
    .stoggle__speaker {
        width: 55%;
        height: 55%;
        fill: none;
        stroke: none;
        position: relative;
        z-index: 1;
    }

    .speaker-body {
        fill: hsl(
            calc(40 - (var(--on) * 20)) calc((20 + (var(--on) * 50)) * 1%)
                calc((25 + (var(--on) * 10)) * 1%)
        );
        transition: fill var(--speed) var(--easing);
    }

    .sound-toggle:focus-visible {
        outline: 3px solid rgba(0, 176, 255, 0.4);
        outline-offset: 3px;
    }

    /* ─── Reduced motion ─── */
    @media (prefers-reduced-motion: reduce) {
        .sound-toggle,
        .stoggle__backdrop,
        .stoggle__indicator,
        .stoggle__star,
        .stoggle__star::before,
        .stoggle__disc,
        .mute-slash,
        .notes path,
        .notes g,
        .zzz-marks text,
        .speaker-body {
            transition: none !important;
            animation: none !important;
        }
    }
</style>
